/**
 * Get current UTC offset for a timezone
 * @param {string} timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @returns {string} - UTC offset (e.g., 'UTC-5' or 'UTC+1')
 */
export const getTimezoneOffset = (timezone) => {
  try {
    const date = new Date();

    // Primary: Use Intl.DateTimeFormat with longOffset
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset',
    });

    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find(part => part.type === 'timeZoneName');

    if (offsetPart && offsetPart.value.includes('GMT')) {
      const offset = offsetPart.value.replace('GMT', 'UTC');
      return offset === 'UTC' ? 'UTC+0' : offset;
    }

    // Fallback: Use short offset format if longOffset is not supported
    const shortFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });

    const shortParts = shortFormatter.formatToParts(date);
    const shortOffsetPart = shortParts.find(part => part.type === 'timeZoneName');

    if (shortOffsetPart && shortOffsetPart.value) {
      // Extract offset from formats like "GMT-5" or "GMT+9"
      const match = shortOffsetPart.value.match(/GMT([+-]?\d+)/);
      if (match) {
        const offsetValue = match[1];
        if (offsetValue === '0' || offsetValue === '+0' || offsetValue === '-0') {
          return 'UTC+0';
        }
        const sign = offsetValue.startsWith('-') ? '' : '+';
        return `UTC${sign}${offsetValue}`;
      }
    }

    // Final fallback
    return 'UTC';
  } catch (error) {
    return 'UTC';
  }
};

/**
 * Common timezones grouped by region with display labels
 * Each timezone includes:
 * - key: IANA timezone identifier
 * - label: Human-readable name
 * - region: Geographic region for grouping
 */
export const commonTimezones = [
  // UTC
  { key: 'UTC', label: 'UTC', region: 'Universal' },

  // Americas - North America
  { key: 'America/Los_Angeles', label: 'Pacific Time', region: 'Americas' },
  { key: 'America/Phoenix', label: 'Arizona Time', region: 'Americas' },
  { key: 'America/Denver', label: 'Mountain Time', region: 'Americas' },
  { key: 'America/Chicago', label: 'Central Time', region: 'Americas' },
  { key: 'America/New_York', label: 'Eastern Time', region: 'Americas' },
  { key: 'America/Anchorage', label: 'Alaska Time', region: 'Americas' },
  { key: 'Pacific/Honolulu', label: 'Hawaii Time', region: 'Americas' },

  // Americas - Canada
  { key: 'America/Toronto', label: 'Toronto (Eastern)', region: 'Americas' },
  { key: 'America/Vancouver', label: 'Vancouver (Pacific)', region: 'Americas' },
  { key: 'America/Halifax', label: 'Halifax (Atlantic)', region: 'Americas' },

  // Americas - Latin America
  { key: 'America/Mexico_City', label: 'Mexico City', region: 'Americas' },
  { key: 'America/Sao_Paulo', label: 'São Paulo', region: 'Americas' },
  { key: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'Americas' },
  { key: 'America/Santiago', label: 'Santiago', region: 'Americas' },
  { key: 'America/Bogota', label: 'Bogotá', region: 'Americas' },

  // Europe - Western
  { key: 'Europe/London', label: 'London', region: 'Europe' },
  { key: 'Europe/Dublin', label: 'Dublin', region: 'Europe' },
  { key: 'Europe/Lisbon', label: 'Lisbon', region: 'Europe' },

  // Europe - Central
  { key: 'Europe/Paris', label: 'Paris', region: 'Europe' },
  { key: 'Europe/Berlin', label: 'Berlin', region: 'Europe' },
  { key: 'Europe/Rome', label: 'Rome', region: 'Europe' },
  { key: 'Europe/Madrid', label: 'Madrid', region: 'Europe' },
  { key: 'Europe/Amsterdam', label: 'Amsterdam', region: 'Europe' },
  { key: 'Europe/Brussels', label: 'Brussels', region: 'Europe' },
  { key: 'Europe/Zurich', label: 'Zurich', region: 'Europe' },

  // Europe - Eastern
  { key: 'Europe/Athens', label: 'Athens', region: 'Europe' },
  { key: 'Europe/Istanbul', label: 'Istanbul', region: 'Europe' },
  { key: 'Europe/Moscow', label: 'Moscow', region: 'Europe' },
  
  // Asia - East Asia
  { key: 'Asia/Manila', label: 'Manila', region: 'Asia' },
  { key: 'Asia/Singapore', label: 'Singapore', region: 'Asia' },
  { key: 'Asia/Shanghai', label: 'Beijing / Shanghai', region: 'Asia' },
  { key: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'Asia' },
  { key: 'Asia/Seoul', label: 'Seoul', region: 'Asia' },
  { key: 'Asia/Tokyo', label: 'Tokyo', region: 'Asia' },

  // Asia - Middle East
  { key: 'Asia/Dubai', label: 'Dubai', region: 'Asia' },
  { key: 'Asia/Jerusalem', label: 'Jerusalem', region: 'Asia' },

  // Asia - South Asia
  { key: 'Asia/Kolkata', label: 'Mumbai / Delhi', region: 'Asia' },
  { key: 'Asia/Karachi', label: 'Karachi', region: 'Asia' },

  // Pacific
  { key: 'Australia/Sydney', label: 'Sydney', region: 'Pacific' },
  { key: 'Australia/Melbourne', label: 'Melbourne', region: 'Pacific' },
  { key: 'Pacific/Auckland', label: 'Auckland', region: 'Pacific' },
];

/**
 * Get formatted timezone options for display in dropdown
 * Includes timezone name with current UTC offset
 * @returns {Array} - Array of objects with key and displayLabel
 */
export const getTimezoneOptions = () => {
  return commonTimezones.map(tz => {
    const offset = getTimezoneOffset(tz.key);
    return {
      key: tz.key,
      label: tz.label,
      displayLabel: `${tz.label} (${offset})`,
      region: tz.region,
    };
  });
};

/**
 * Legacy config for backward compatibility
 */
export const TimezonesConfig = {
  default: 'UTC',
};
